/**
 * PretreatFeedback - Visual feedback for pretreat scan results
 *
 * Displays scan result with:
 * - Color-coded borders (green success, yellow warning, red error)
 * - Large icons for warehouse visibility
 * - Auto-clear after 3 seconds
 * - role="alert" for error states (screen reader announcement)
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatOrderNumber } from '@threadheads/shared';

type PretreatScanStatus =
  | 'success'
  | 'already_scanned'
  | 'no_pretreat_needed'
  | 'not_pod_item'
  | 'order_cancelled'
  | 'order_on_hold';

interface GarmentInfo {
  id: string;
  productName: string;
  size: string | null;
  orderName: string;
}

interface PretreatScanResult {
  status: PretreatScanStatus;
  garment?: GarmentInfo;
  holdReason?: string | null;
}

interface PretreatFeedbackProps {
  result: PretreatScanResult;
  onDismiss: () => void;
}

function useStatusConfig() {
  const { t } = useTranslation('production');

  const STATUS_CONFIG: Record<PretreatScanStatus, {
    variant: 'default' | 'destructive';
    icon: typeof CheckCircle;
    iconClass: string;
    borderClass: string;
    bgClass: string;
    title: string;
    getMessage: (garment?: GarmentInfo) => string;
    isError: boolean;
  }> = {
    success: {
      variant: 'default',
      icon: CheckCircle,
      iconClass: 'text-green-600',
      borderClass: 'border-green-500',
      bgClass: 'bg-green-50 dark:bg-green-950/30',
      title: t('pretreat.scanRecorded'),
      getMessage: (garment) => garment
        ? `${garment.productName}${garment.size ? ` - ${garment.size}` : ''} (${formatOrderNumber(garment.orderName)})`
        : t('pretreat.garmentReady'),
      isError: false,
    },
    already_scanned: {
      variant: 'default',
      icon: AlertTriangle,
      iconClass: 'text-amber-600',
      borderClass: 'border-amber-500',
      bgClass: 'bg-amber-50 dark:bg-amber-950/30',
      title: t('pretreat.alreadyScanned'),
      getMessage: (garment) => garment
        ? t('pretreat.alreadyScannedDesc', { product: garment.productName })
        : t('pretreat.alreadyScannedShort'),
      isError: false,
    },
    no_pretreat_needed: {
      variant: 'destructive',
      icon: AlertCircle,
      iconClass: 'text-red-600',
      borderClass: 'border-red-500',
      bgClass: 'bg-red-50 dark:bg-red-950/30',
      title: t('pretreat.noPretreatNeeded'),
      getMessage: () => t('pretreat.noPretreatNeededLight'),
      isError: true,
    },
    not_pod_item: {
      variant: 'destructive',
      icon: AlertCircle,
      iconClass: 'text-red-600',
      borderClass: 'border-red-500',
      bgClass: 'bg-red-50 dark:bg-red-950/30',
      title: t('pretreat.notPodItem'),
      getMessage: () => t('pretreat.notPodItemDesc'),
      isError: true,
    },
    order_cancelled: {
      variant: 'destructive',
      icon: AlertCircle,
      iconClass: 'text-red-600',
      borderClass: 'border-red-500',
      bgClass: 'bg-red-50 dark:bg-red-950/30',
      title: t('pretreat.orderCancelled'),
      getMessage: () => t('pretreat.orderCancelledDesc'),
      isError: true,
    },
    // Part 3: amber (warning) — on hold is reversible/non-terminal, unlike cancelled (red).
    order_on_hold: {
      variant: 'default',
      icon: AlertTriangle,
      iconClass: 'text-amber-600',
      borderClass: 'border-amber-500',
      bgClass: 'bg-amber-50 dark:bg-amber-950/30',
      title: t('pretreat.orderOnHold'),
      getMessage: () => t('pretreat.orderOnHoldDesc'),
      isError: true,
    },
  };

  return STATUS_CONFIG;
}

export function PretreatFeedback({ result, onDismiss }: PretreatFeedbackProps) {
  const { t } = useTranslation('production');
  const STATUS_CONFIG = useStatusConfig();
  const config = STATUS_CONFIG[result.status];

  // Auto-clear after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const Icon = config.icon;

  return (
    <Alert
      variant={config.variant}
      className={cn(
        'relative border-2',
        config.borderClass,
        config.bgClass
      )}
      role={config.isError ? 'alert' : undefined}
    >
      <Icon className={cn('h-12 w-12', config.iconClass)} />
      <AlertTitle className="text-lg font-semibold ml-4">
        {config.title}
      </AlertTitle>
      <AlertDescription className="text-base ml-4">
        {config.getMessage(result.garment)}
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2"
        onClick={onDismiss}
        aria-label={t('pretreat.dismiss')}
      >
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
}
